import Instrument from "./instrument";
import GraphicalTransformer from "../transformer";
import { getTransform, checkModifier, setLinkSelectionPredicate, } from "../helpers";
import * as d3 from "d3";
import Command from "../command/command";
const DSL_INSTRUMENT_ATTR = "data-libra-dsl-instrument";
function normalizeDslInstrumentName(value) {
    if (typeof value !== "string")
        return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}
function clearScopedLayerGraphic(graphic, dslInstanceName) {
    if (!graphic)
        return;
    const scope = normalizeDslInstrumentName(dslInstanceName);
    const selection = d3
        .select(graphic)
        .selectAll(":not(.ig-layer-background)");
    if (!scope) {
        selection.remove();
        return;
    }
    selection
        .filter(function () {
        return (this.getAttribute(DSL_INSTRUMENT_ATTR) === scope);
    })
        .remove();
}
Instrument.register("HoverInstrument", {
    constructor: Instrument,
    interactors: ["MousePositionInteractor", "TouchPositionInteractor"],
    on: {
        hover: [
            async ({ event, layer, instrument, pickingResult }) => {
                // Access pre-calculated features
                // const features = (event as any).libraFeatures;
                // if (features && features.dwellTime > 1000) { console.log("Long dwell!"); }
                // console.log("[HoverInstrument Debug]", pickingResult && pickingResult.length > 0 ? "Hit Element" : "Hit Empty", pickingResult);
                const modifierKey = instrument.getSharedVar("modifierKey");
                if (!checkModifier(event, modifierKey))
                    return;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const services = instrument.services.find("SelectionService");
                services.setSharedVars({
                    event,
                    x: event.clientX,
                    y: event.clientY,
                    offsetx: event.offsetX,
                    offsety: event.offsetY,
                }, { layer });
                const transformers = instrument.transformers;
                transformers.setSharedVars({ cx: event.clientX, cy: event.clientY });
                const linkLayers = instrument.getSharedVar("linkLayers");
                if (Array.isArray(linkLayers) && linkLayers.length > 0) {
                    const sourceId = String(instrument.getSharedVar("linkSelectionSource") ??
                        layer._name ??
                        instrument._name);
                    const root = layer?.getGraphic?.();
                    const hit = Array.isArray(pickingResult)
                        ? pickingResult.find((n) => n && n !== root)
                        : null;
                    if (!hit) {
                        setLinkSelectionPredicate(sourceId, null);
                        return;
                    }
                    const datum = layer.getDatum?.(hit) ?? hit.__data__ ?? null;
                    if (!datum || typeof datum !== "object") {
                        setLinkSelectionPredicate(sourceId, null);
                        return;
                    }
                    const linkMatchModeRaw = instrument.getSharedVar("linkMatchMode") ??
                        instrument.getSharedVar("linkMatch") ??
                        instrument.getSharedVar("linkMatchStrategy");
                    const linkMatchMode = typeof linkMatchModeRaw === "string"
                        ? linkMatchModeRaw.trim().toLowerCase()
                        : "field";
                    if (linkMatchMode === "datum" || linkMatchMode === "data") {
                        setLinkSelectionPredicate(sourceId, { __libra_datum__: datum });
                        return;
                    }
                    const normalizeFields = (raw) => {
                        if (typeof raw === "string" && raw.trim())
                            return [raw.trim()];
                        if (Array.isArray(raw))
                            return raw
                                .filter((s) => typeof s === "string" && s.trim())
                                .map((s) => String(s).trim());
                        return [];
                    };
                    const rawLinkFields = instrument.getSharedVar("linkFields") ??
                        instrument.getSharedVar("linkField") ??
                        instrument.getSharedVar("linkBy") ??
                        instrument.getSharedVar("attrName") ??
                        instrument.getSharedVar("linkAttrName") ??
                        instrument.getSharedVar("linkAttr");
                    let linkFields = normalizeFields(rawLinkFields);
                    if (linkFields.length === 0) {
                        const candidates = ["id", "ID", "Id", "key", "Key", "name", "Name"];
                        const found = candidates.find((k) => Object.prototype.hasOwnProperty.call(datum, k));
                        if (found)
                            linkFields = [found];
                    }
                    const predicate = {};
                    linkFields.forEach((field) => {
                        const v = datum[field];
                        if (typeof v === "number") {
                            if (Number.isFinite(v))
                                predicate[field] = v;
                            return;
                        }
                        if (typeof v === "string" || typeof v === "boolean") {
                            predicate[field] = v;
                        }
                    });
                    setLinkSelectionPredicate(sourceId, Object.keys(predicate).length ? predicate : null);
                }
            },
        ],
        click: [Command.initialize("Log", { execute() { } })],
    },
    // postInitialize: (instrument) => {
    //   instrument.services.add("SurfacePointSelectionService", {
    //     // layer,
    //     sharedVar: {
    //       deepClone: instrument.getSharedVar("deepClone"),
    //       highlightColor: instrument.getSharedVar("highlightColor"),
    //       highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
    //       tooltip: instrument.getSharedVar("tooltip"),
    //     },
    //   });
    // },
    preAttach: (instrument, layer) => {
        const linkLayers = instrument.getSharedVar("linkLayers");
        if (Array.isArray(linkLayers) && linkLayers.length > 0) {
            const allLayers = linkLayers.filter((l, i, arr) => {
                return !!l && arr.indexOf(l) === i;
            });
            allLayers.forEach((l) => {
                GraphicalTransformer.initialize("LinkSelectionHubTransformer", {
                    layer: l,
                    sharedVar: {
                        linkStrokeColor: instrument.getSharedVar("linkStrokeColor"),
                        linkStrokeWidth: instrument.getSharedVar("linkStrokeWidth"),
                        highlightColor: instrument.getSharedVar("highlightColor"),
                        highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                        tooltip: instrument.getSharedVar("tooltip"),
                    },
                });
            });
        }
        if (layer.onUpdate) {
            layer.onUpdate((layer) => {
                const services = instrument.services.find("SelectionService");
                if (services) {
                    // Force clear result and re-evaluate
                    services._result = [];
                    services._evaluate(layer);
                }
            });
        }
        const hasDim = Object.prototype.hasOwnProperty.call(instrument._sharedVar, "dim");
        const renderSelection = instrument.getSharedVar("renderSelection");
        instrument.services.add("SurfacePointSelectionService", {
            layer,
            renderSelection,
            sharedVar: {
                deepClone: instrument.getSharedVar("deepClone"),
                highlightColor: instrument.getSharedVar("highlightColor"),
                highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                tooltip: instrument.getSharedVar("tooltip"),
                data: instrument.getSharedVar("data"),
                ...(hasDim ? { dim: instrument.getSharedVar("dim") } : {}),
            },
        });
    },
});
Instrument.register("ClickInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            async (options) => {
                let { event, layer, instrument } = options;
                const modifierKey = instrument.getSharedVar("modifierKey");
                if (!checkModifier(event, modifierKey)) {
                    instrument.setSharedVar("interactionValid", false);
                    return;
                }
                instrument.setSharedVar("interactionValid", true);
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.setSharedVar("x", event.clientX);
                instrument.setSharedVar("y", event.clientY);
                const services = instrument.services.find("SelectionService");
                services.setSharedVars({
                    x: event.clientX,
                    y: event.clientY,
                    offsetx: event.offsetX,
                    offsety: event.offsetY,
                }, { layer });
                instrument.emit("clickstart", {
                    ...options,
                    self: options.instrument,
                });
            },
            Command.initialize("Log", { execute() { } }),
        ],
        dragend: [
            async (options) => {
                let { event, layer, instrument } = options;
                if (!instrument.getSharedVar("interactionValid"))
                    return;
                const modifierKey = instrument.getSharedVar("modifierKey");
                if (!checkModifier(event, modifierKey))
                    return;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const services = instrument.services.find("SelectionService");
                services.setSharedVars({
                    x: 0,
                    y: 0,
                    offsetx: 0,
                    offsety: 0,
                }, { layer });
                if (event.clientX === instrument.getSharedVar("x") &&
                    event.clientY === instrument.getSharedVar("y")) {
                    instrument.setSharedVar("x", 0);
                    instrument.setSharedVar("y", 0);
                    instrument.emit("click", {
                        ...options,
                        self: options.instrument,
                    });
                }
                else {
                    instrument.setSharedVar("x", 0);
                    instrument.setSharedVar("y", 0);
                    instrument.emit("clickabort", {
                        ...options,
                        self: options.instrument,
                    });
                }
            },
        ],
        dragabort: [
            (options) => {
                if (options.event.changedTouches)
                    options.event = options.event.changedTouches[0];
                const services = options.instrument.services.find("SelectionService");
                services.setSharedVars({
                    x: 0,
                    y: 0,
                    offsetx: 0,
                    offsety: 0,
                }, { layer: options.layer });
                options.instrument.emit("clickabort", {
                    ...options,
                    self: options.instrument,
                });
            },
        ],
    },
    preAttach: (instrument, layer) => {
        const hasDim = Object.prototype.hasOwnProperty.call(instrument._sharedVar, "dim");
        instrument.services.add("SurfacePointSelectionService", {
            layer,
            sharedVar: {
                deepClone: instrument.getSharedVar("deepClone"),
                highlightColor: instrument.getSharedVar("highlightColor"),
                highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                ...(hasDim ? { dim: instrument.getSharedVar("dim") } : {}),
            },
        });
    },
});
Instrument.register("BrushInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            async ({ event, layer, instrument }) => {
                const modifierKey = instrument.getSharedVar("modifierKey");
                if (!checkModifier(event, modifierKey)) {
                    instrument.setSharedVar("interactionValid", false);
                    return;
                }
                instrument.setSharedVar("interactionValid", true);
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const services = instrument.services.find("RectSelectionService");
                // Initialize selection history if not present
                let selectionHistory = instrument.getSharedVar("selectionHistory");
                if (!selectionHistory) {
                    selectionHistory = [];
                    instrument.setSharedVar("selectionHistory", selectionHistory);
                }
                const remnantKey = instrument.getSharedVar("remnantKey");
                // Clear history if NOT merging.
                // Merging happens only if remnantKey is defined AND pressed.
                // If remnantKey is undefined (default single selection) or not pressed (transient/new selection), clear history.
                const isMerging = remnantKey && checkModifier(event, remnantKey);
                if (!isMerging) {
                    selectionHistory = [];
                    instrument.setSharedVar("selectionHistory", selectionHistory);
                }
                services.setSharedVars({
                    x: event.clientX,
                    y: event.clientY,
                    offsetx: event.offsetX,
                    offsety: event.offsetY,
                    width: 1,
                    height: 1,
                    startx: event.clientX,
                    starty: event.clientY,
                    startoffsetx: event.offsetX,
                    startoffsety: event.offsetY,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    selectionHistory: selectionHistory, // Pass history to service
                }, { layer });
                const x = event.offsetX;
                const y = event.offsetY;
                const width = 0;
                const height = 0;
                const layerOffsetX = layer._offset?.x ?? 0;
                const layerOffsetY = layer._offset?.y ?? 0;
                const scaleX = instrument.getSharedVar("scaleX");
                const scaleY = instrument.getSharedVar("scaleY");
                if (scaleX && scaleX.invert && scaleY && scaleY.invert) {
                    const newExtentX = [x - layerOffsetX, x - layerOffsetX + width].map(scaleX.invert);
                    const newExtentY = [y - layerOffsetY, y - layerOffsetY + height].map(scaleY.invert);
                    instrument.setSharedVar("extent", [newExtentX, newExtentY], {
                        layer,
                    });
                    instrument.services
                        .find("SelectionService")
                        .filter([newExtentX, newExtentY]);
                }
                else {
                    instrument.services.find("SelectionService").filter([
                        [x - layerOffsetX, x - layerOffsetX + width],
                        [y - layerOffsetY, y - layerOffsetY + height],
                    ]);
                }
                instrument.setSharedVar("startx", event.clientX);
                instrument.setSharedVar("starty", event.clientY);
                instrument.setSharedVar("startoffsetx", event.offsetX);
                instrument.setSharedVar("startoffsety", event.offsetY);
            },
        ],
        drag: [
            async (options) => {
                console.log("brush");
                let { event, layer, instrument } = options;
                if (!instrument.getSharedVar("interactionValid"))
                    return;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const startx = instrument.getSharedVar("startx");
                const starty = instrument.getSharedVar("starty");
                const startoffsetx = instrument.getSharedVar("startoffsetx");
                const startoffsety = instrument.getSharedVar("startoffsety");
                const x = Math.min(startx, event.clientX);
                const y = Math.min(starty, event.clientY);
                // Use delta from startx/starty to calculate offset, avoiding offsetX inconsistencies due to event target changes
                const diffx = event.clientX - startx;
                const diffy = event.clientY - starty;
                const offsetx = Math.min(startoffsetx, startoffsetx + diffx);
                const offsety = Math.min(startoffsety, startoffsety + diffy);
                const width = Math.abs(diffx);
                const height = Math.abs(diffy);
                // selection, currently service use client coordinates, but coordinates relative to the layer maybe more appropriate.
                const services = instrument.services.find("SelectionService");
                services.setSharedVars({
                    x,
                    y,
                    offsetx,
                    offsety,
                    width,
                    height,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    remnantKey: instrument.getSharedVar("remnantKey"),
                    event: event,
                }, { layer });
            },
        ],
        dragend: [
            async (options) => {
                const { event, layer, instrument } = options;
                const remnantKey = instrument.getSharedVar("remnantKey");
                const inputEvent = event.changedTouches ? event.changedTouches[0] : event;
                let selectionHistory = instrument.getSharedVar("selectionHistory") || [];
                if (remnantKey && !checkModifier(event, remnantKey)) {
                    // If remnantKey is set but not pressed, clear the selection (same logic as dragabort)
                    // Also clear history as we are resetting
                    selectionHistory = [];
                    instrument.setSharedVar("selectionHistory", selectionHistory);
                    const services = instrument.services.find("RectSelectionService");
                    services.setSharedVars({
                        x: 0,
                        y: 0,
                        offsetx: 0,
                        offsety: 0,
                        width: 0,
                        height: 0,
                        currentx: inputEvent.clientX,
                        currenty: inputEvent.clientY,
                        endx: inputEvent.clientX,
                        endy: inputEvent.clientY,
                        selectionHistory: [],
                    }, { layer });
                    instrument.emit("brushabort", options);
                }
                else {
                    // Default behavior: keep selection
                    // Save current selection to history
                    // We need to calculate the final rect relative to the start point
                    const startx = instrument.getSharedVar("startx");
                    const starty = instrument.getSharedVar("starty");
                    const startoffsetx = instrument.getSharedVar("startoffsetx");
                    const startoffsety = instrument.getSharedVar("startoffsety");
                    const x = Math.min(startx, inputEvent.clientX);
                    const y = Math.min(starty, inputEvent.clientY);
                    // Use delta from startx/starty to calculate offset, avoiding offsetX inconsistencies
                    const diffx = inputEvent.clientX - startx;
                    const diffy = inputEvent.clientY - starty;
                    const offsetx = Math.min(startoffsetx, startoffsetx + diffx);
                    const offsety = Math.min(startoffsety, startoffsety + diffy);
                    const width = Math.abs(diffx);
                    const height = Math.abs(diffy);
                    const layerOffsetX = layer._offset?.x ?? 0;
                    const layerOffsetY = layer._offset?.y ?? 0;
                    // Add to history
                    selectionHistory.push({
                        x,
                        y,
                        offsetx: offsetx - layerOffsetX,
                        offsety: offsety - layerOffsetY,
                        width,
                        height
                    });
                    instrument.setSharedVar("selectionHistory", selectionHistory);
                    console.log("[BrushInstrument] Selection History:", selectionHistory);
                    Command.initialize("Log", { execute() { } }).execute(options);
                }
            },
        ],
        dragabort: [
            async (options) => {
                let { event, layer, instrument } = options;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const services = instrument.services.find("SelectionService");
                services.setSharedVars({
                    x: 0,
                    y: 0,
                    offsetx: 0,
                    offsety: 0,
                    width: 0,
                    height: 0,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    endx: event.clientX,
                    endy: event.clientY,
                }, { layer });
                instrument.emit("brushabort", options);
            },
        ],
    },
    preAttach: (instrument, layer) => {
        // create selectionLayer first
        const selectionLayer = layer.getLayerFromQueue("selectionLayer");
        const linkLayers = instrument.getSharedVar("linkLayers");
        const dslInstanceName = instrument.getSharedVar("dslInstanceName");
        console.log("[BrushInstrument Debug] preAttach", {
            instrumentName: instrument._name,
            instrumentBaseName: instrument._baseName,
            layerName: layer?._name,
            dslInstrumentName: instrument.getSharedVar("dslInstrumentName"),
            dslInstanceName,
        });
        const hasDim = Object.prototype.hasOwnProperty.call(instrument._sharedVar, "dim");
        if (Array.isArray(linkLayers) && linkLayers.length > 0) {
            const allLayers = [layer, ...linkLayers].filter((l, i, arr) => !!l && arr.indexOf(l) === i);
            allLayers.forEach((l) => {
                GraphicalTransformer.initialize("LinkSelectionHubTransformer", {
                    layer: l,
                    sharedVar: {
                        linkStrokeColor: instrument.getSharedVar("linkStrokeColor"),
                        linkStrokeWidth: instrument.getSharedVar("linkStrokeWidth"),
                        highlightColor: instrument.getSharedVar("highlightColor"),
                        highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                        tooltip: instrument.getSharedVar("tooltip"),
                    },
                });
            });
        }
        // Sync selection layer with parent layer updates
        if (layer.onUpdate) {
            layer.onUpdate(() => {
                console.log("[BrushInstrument Debug] layer.onUpdate -> _evaluate", {
                    instrumentName: instrument._name,
                    instrumentBaseName: instrument._baseName,
                    layerName: layer?._name,
                    dslInstanceName,
                });
                const graphic = selectionLayer.getGraphic();
                clearScopedLayerGraphic(graphic, dslInstanceName);
                const selectionService = instrument.services.find("RectSelectionService");
                if (selectionService) {
                    selectionService._evaluate(layer);
                }
            });
        }
        instrument.services.add("RectSelectionService", {
            layer,
            sharedVar: {
                deepClone: instrument.getSharedVar("deepClone"),
                ...(Array.isArray(linkLayers) && linkLayers.length > 0
                    ? {
                        linkSelection: true,
                        linkSelectionSource: String(dslInstanceName ?? layer._name ?? instrument._name),
                    }
                    : {}),
                ...(dslInstanceName
                    ? { dslInstanceName: dslInstanceName }
                    : {}),
                ...(instrument.getSharedVar("highlightColor")
                    ? { highlightColor: instrument.getSharedVar("highlightColor") }
                    : {}),
                ...(instrument.getSharedVar("highlightAttrValues")
                    ? {
                        highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                    }
                    : {}),
                ...(instrument.getSharedVar("brushStyle")
                    ? { brushStyle: instrument.getSharedVar("brushStyle") }
                    : {}),
                ...(instrument.getSharedVar("scaleX")
                    ? { scaleX: instrument.getSharedVar("scaleX") }
                    : {}),
                ...(instrument.getSharedVar("scaleY")
                    ? { scaleY: instrument.getSharedVar("scaleY") }
                    : {}),
                ...(instrument.getSharedVar("attrName")
                    ? { attrName: instrument.getSharedVar("attrName") }
                    : {}),
                ...(Array.isArray(linkLayers) && linkLayers.length > 0
                    ? { linkLayers: linkLayers }
                    : {}),
                ...(instrument.getSharedVar("linkDefaultOpacity") !== undefined
                    ? { linkDefaultOpacity: instrument.getSharedVar("linkDefaultOpacity") }
                    : {}),
                ...(instrument.getSharedVar("linkBaseOpacity") !== undefined
                    ? { linkBaseOpacity: instrument.getSharedVar("linkBaseOpacity") }
                    : {}),
                ...(instrument.getSharedVar("linkSelectedOpacity") !== undefined
                    ? { linkSelectedOpacity: instrument.getSharedVar("linkSelectedOpacity") }
                    : {}),
                ...(instrument.getSharedVar("linkStrokeColor") !== undefined
                    ? { linkStrokeColor: instrument.getSharedVar("linkStrokeColor") }
                    : {}),
                ...(instrument.getSharedVar("linkStrokeWidth") !== undefined
                    ? { linkStrokeWidth: instrument.getSharedVar("linkStrokeWidth") }
                    : {}),
                ...(hasDim ? { dim: instrument.getSharedVar("dim") } : {}),
            },
        });
    },
});
Instrument.register("BrushXInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            async ({ event, layer, instrument }) => {
                const modifierKey = instrument.getSharedVar("modifierKey");
                if (!checkModifier(event, modifierKey)) {
                    instrument.setSharedVar("interactionValid", false);
                    return;
                }
                instrument.setSharedVar("interactionValid", true);
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const services = instrument.services;
                services.setSharedVars({
                    x: event.clientX,
                    offsetx: event.offsetX,
                    width: 0,
                    startx: event.clientX,
                    startoffsetx: event.offsetX,
                    currentx: event.clientX,
                }, { layer });
                instrument.setSharedVar("startx", event.clientX);
                instrument.setSharedVar("startoffsetx", event.offsetX);
            },
        ],
        drag: [
            async (options) => {
                let { event, layer, instrument } = options;
                if (!instrument.getSharedVar("interactionValid"))
                    return;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const startx = instrument.getSharedVar("startx");
                const startoffsetx = instrument.getSharedVar("startoffsetx");
                const x = Math.min(startx, event.clientX);
                const offsetx = Math.min(startoffsetx, event.offsetX);
                const width = Math.abs(event.clientX - startx);
                // selection, currently service use client coordinates, but coordinates relative to the layer maybe more appropriate.
                instrument.services.find("SelectionService").setSharedVars({
                    x,
                    offsetx,
                    width,
                    currentx: event.clientX,
                }, { layer });
                instrument.setSharedVar("currentx", event.clientX);
                instrument.setSharedVar("currentoffsetx", event.offsetX);
                instrument.emit("brush", options);
            },
        ],
        dragend: [Command.initialize("Log", { execute() { } })],
        dragabort: [
            async (options) => {
                let { event, layer, instrument } = options;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.services.setSharedVars({
                    x: 0,
                    offsetx: 0,
                    width: 0,
                    currentx: event.clientX,
                }, { layer });
                instrument.emit("brushabort", options);
            },
        ],
    },
    preAttach: (instrument, layer) => {
        // create selectionLayer first
        const selectionLayer = layer.getLayerFromQueue("selectionLayer");
        const linkLayers = instrument.getSharedVar("linkLayers");
        const hasDim = Object.prototype.hasOwnProperty.call(instrument._sharedVar, "dim");
        if (Array.isArray(linkLayers) && linkLayers.length > 0) {
            const allLayers = [layer, ...linkLayers].filter((l, i, arr) => !!l && arr.indexOf(l) === i);
            allLayers.forEach((l) => {
                GraphicalTransformer.initialize("LinkSelectionHubTransformer", {
                    layer: l,
                    sharedVar: {
                        linkStrokeColor: instrument.getSharedVar("linkStrokeColor"),
                        linkStrokeWidth: instrument.getSharedVar("linkStrokeWidth"),
                        highlightColor: instrument.getSharedVar("highlightColor"),
                        highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                        tooltip: instrument.getSharedVar("tooltip"),
                    },
                });
            });
        }
        // Sync selection layer with parent layer updates
        if (layer.onUpdate) {
            layer.onUpdate(() => {
                // console.log(
                //   "[BrushXInstrument] Parent layer updated. Re-evaluating selection..."
                // );
                const graphic = selectionLayer.getGraphic();
                if (graphic)
                    graphic.innerHTML = "";
                const selectionService = instrument.services.find("RectSelectionService");
                if (selectionService) {
                    selectionService._evaluate(layer);
                }
            });
        }
        instrument.services.add("RectSelectionService", {
            layer,
            sharedVar: {
                deepClone: instrument.getSharedVar("deepClone"),
                highlightColor: instrument.getSharedVar("highlightColor"),
                highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                ...(Array.isArray(linkLayers) && linkLayers.length > 0
                    ? {
                        linkSelection: true,
                        linkSelectionSource: String(layer._name ?? instrument._name),
                        linkLayers: linkLayers,
                    }
                    : {}),
                ...(instrument.getSharedVar("brushStyle")
                    ? { brushStyle: instrument.getSharedVar("brushStyle") }
                    : {}),
                ...(hasDim ? { dim: instrument.getSharedVar("dim") } : {}),
            },
        });
    },
});
Instrument.register("BrushYInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            async ({ event, layer, instrument }) => {
                const modifierKey = instrument.getSharedVar("modifierKey");
                if (!checkModifier(event, modifierKey)) {
                    instrument.setSharedVar("interactionValid", false);
                    return;
                }
                instrument.setSharedVar("interactionValid", true);
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const services = instrument.services;
                services.setSharedVars({
                    y: event.clientY,
                    offsety: event.offsetY,
                    height: 0,
                    starty: event.clientY,
                    startoffsety: event.offsetY,
                    currenty: event.clientY,
                }, { layer });
                instrument.setSharedVar("starty", event.clientY);
                instrument.setSharedVar("startoffsety", event.offsetY);
            },
        ],
        drag: [
            async (options) => {
                let { event, layer, instrument } = options;
                if (!instrument.getSharedVar("interactionValid"))
                    return;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const starty = instrument.getSharedVar("starty");
                const startoffsety = instrument.getSharedVar("startoffsety");
                const y = Math.min(starty, event.clientY);
                const offsety = Math.min(startoffsety, event.offsetY);
                const height = Math.abs(event.clientY - starty);
                // selection, currently service use client coordinates, but coordinates relative to the layer maybe more appropriate.
                instrument.services.find("SelectionService").setSharedVars({
                    y,
                    offsety,
                    height,
                    currenty: event.clientY,
                }, { layer });
                instrument.setSharedVar("currenty", event.clientY);
                instrument.setSharedVar("currentoffsety", event.offsetY);
                instrument.emit("brush", options);
            },
        ],
        dragend: [Command.initialize("Log", { execute() { } })],
        dragabort: [
            async (options) => {
                let { event, layer, instrument } = options;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.services.setSharedVars({
                    y: 0,
                    offsety: 0,
                    height: 0,
                    currenty: event.clientY,
                }, { layer });
                instrument.emit("brushabort", options);
            },
        ],
    },
    preAttach: (instrument, layer) => {
        // create selectionLayer first
        const selectionLayer = layer.getLayerFromQueue("selectionLayer");
        const linkLayers = instrument.getSharedVar("linkLayers");
        const hasDim = Object.prototype.hasOwnProperty.call(instrument._sharedVar, "dim");
        if (Array.isArray(linkLayers) && linkLayers.length > 0) {
            const allLayers = [layer, ...linkLayers].filter((l, i, arr) => !!l && arr.indexOf(l) === i);
            allLayers.forEach((l) => {
                GraphicalTransformer.initialize("LinkSelectionHubTransformer", {
                    layer: l,
                    sharedVar: {
                        linkStrokeColor: instrument.getSharedVar("linkStrokeColor"),
                        linkStrokeWidth: instrument.getSharedVar("linkStrokeWidth"),
                        highlightColor: instrument.getSharedVar("highlightColor"),
                        highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                        tooltip: instrument.getSharedVar("tooltip"),
                    },
                });
            });
        }
        // Sync selection layer with parent layer updates
        if (layer.onUpdate) {
            layer.onUpdate(() => {
                // console.log(
                //   "[BrushYInstrument] Parent layer updated. Re-evaluating selection..."
                // );
                const graphic = selectionLayer.getGraphic();
                if (graphic)
                    graphic.innerHTML = "";
                const selectionService = instrument.services.find("RectSelectionService");
                if (selectionService) {
                    selectionService._evaluate(layer);
                }
            });
        }
        instrument.services.add("RectSelectionService", {
            layer,
            sharedVar: {
                deepClone: instrument.getSharedVar("deepClone"),
                highlightColor: instrument.getSharedVar("highlightColor"),
                highlightAttrValues: instrument.getSharedVar("highlightAttrValues"),
                ...(Array.isArray(linkLayers) && linkLayers.length > 0
                    ? {
                        linkSelection: true,
                        linkSelectionSource: String(layer._name ?? instrument._name),
                        linkLayers: linkLayers,
                    }
                    : {}),
                ...(instrument.getSharedVar("brushStyle")
                    ? { brushStyle: instrument.getSharedVar("brushStyle") }
                    : {}),
                ...(hasDim ? { dim: instrument.getSharedVar("dim") } : {}),
            },
        });
    },
});
Instrument.register("HelperLineInstrument", {
    constructor: Instrument,
    sharedVar: { orientation: ["horizontal"] },
    interactors: ["MousePositionInteractor", "TouchPositionInteractor"],
    on: {
        hover: [
            ({ event, layer, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.transformers.setSharedVars({
                    x: event.offsetX,
                    y: event.offsetY,
                });
                instrument.setSharedVar("x", event.offsetX, {});
                instrument.setSharedVar("y", event.offsetY, {});
            },
        ],
        click: [Command.initialize("Log", { execute() { } })],
    },
    preAttach: function (instrument, layer) {
        instrument.transformers.add("HelperLineTransformer", {
            layer: layer.getLayerFromQueue("transientLayer"),
            sharedVar: {
                orientation: instrument.getSharedVar("orientation"),
                style: instrument.getSharedVar("style") || {},
                tooltip: instrument.getSharedVar("tooltip"),
                scaleX: instrument.getSharedVar("scaleX"),
                scaleY: instrument.getSharedVar("scaleY"),
            },
        });
    },
});
Instrument.register("DataBrushInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            async ({ event, layer, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const scaleX = instrument.getSharedVar("scaleX");
                const scaleY = instrument.getSharedVar("scaleY");
                const services = instrument.services.find("Quantitative2DSelectionService");
                // services.setSharedVar("x", event.clientX, { layer });
                // services.setSharedVar("width", 1, { layer });
                // const
                // services.setSharedVar("startx", event.clientX, { layer });
                // services.setSharedVar("currentx", event.clientX, { layer });
                const layerPos = d3.pointer(event, layer.getGraphic());
                instrument.setSharedVar("layerOffsetX", event.clientX - layerPos[0]);
                instrument.setSharedVar("layerOffsetY", event.clientY - layerPos[1]);
                instrument.setSharedVar("startx", event.clientX);
                instrument.setSharedVar("starty", event.clientY);
                const newExtentX = [layerPos[0], layerPos[0] + 1].map(scaleX.invert);
                services.setSharedVar("extentX", newExtentX);
                const newExtentY = [layerPos[1], layerPos[1] + 1].map(scaleY.invert);
                services.setSharedVar("extentX", newExtentY);
                instrument.transformers
                    .find("TransientRectangleTransformer")
                    .setSharedVars({
                    x: 0,
                    y: 0,
                    width: 1,
                    height: 1,
                });
            },
        ],
        drag: [
            Command.initialize("drawBrushAndSelect", {
                continuous: true,
                execute: async ({ event, layer, instrument }) => {
                    if (event.changedTouches)
                        event = event.changedTouches[0];
                    const startx = instrument.getSharedVar("startx");
                    const starty = instrument.getSharedVar("starty");
                    const layerOffsetX = instrument.getSharedVar("layerOffsetX");
                    const layerOffsetY = instrument.getSharedVar("layerOffsetY");
                    const scaleX = instrument.getSharedVar("scaleX");
                    const scaleY = instrument.getSharedVar("scaleY");
                    const x = Math.min(startx, event.clientX) - layerOffsetX;
                    const y = Math.min(starty, event.clientY) - layerOffsetY;
                    const width = Math.abs(event.clientX - startx);
                    const height = Math.abs(event.clientY - starty);
                    instrument.setSharedVar("x", x);
                    instrument.setSharedVar("y", y);
                    instrument.setSharedVar("width", width);
                    instrument.setSharedVar("height", height);
                    const newExtentDataX = [x, x + width].map(scaleX.invert);
                    const newExtentDataY = [y + height, y].map(scaleY.invert);
                    const services = instrument.services.find("SelectionService");
                    services.setSharedVar("extentX", newExtentDataX);
                    services.setSharedVar("extentY", newExtentDataY);
                    console.log(services);
                    await Promise.all(instrument.services.results);
                },
                feedback: [
                    async ({ event, layer, instrument }) => {
                        const x = instrument.getSharedVar("x");
                        const y = instrument.getSharedVar("y");
                        const width = instrument.getSharedVar("width");
                        const height = instrument.getSharedVar("height");
                        instrument.transformers
                            .find("TransientRectangleTransformer")
                            .setSharedVars({
                            x: x,
                            y: y,
                            width: width,
                            height: height,
                        });
                    },
                    async ({ instrument }) => {
                        instrument.transformers.find("HighlightSelection").setSharedVars({
                            highlightAttrValues: instrument.getSharedVar("highlightAttrValues") || {},
                        });
                    },
                ],
            }),
        ],
        dragabort: [
            async ({ event, layer, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const services = instrument.services.find("SelectionService");
                services.setSharedVar("x", 0, { layer });
                services.setSharedVar("width", 0, { layer });
                services.setSharedVar("currentx", event.clientX, { layer });
                services.setSharedVar("endx", event.clientX, { layer });
                instrument.transformers
                    .find("TransientRectangleTransformer")
                    .setSharedVars({
                    x: 0,
                    width: 0,
                });
            },
        ],
    },
    preAttach: async (instrument, layer) => {
        // create selectionLayer first
        const selectionLayer = layer.getLayerFromQueue("selectionLayer");
        // Sync selection layer with parent layer updates
        if (layer.onUpdate) {
            layer.onUpdate(() => {
                // console.log(
                //   "[DataBrushInstrument] Parent layer updated. Re-evaluating selection..."
                // );
                const graphic = selectionLayer.getGraphic();
                if (graphic)
                    graphic.innerHTML = "";
                const selectionService = instrument.services.find("Quantitative2DSelectionService");
                if (selectionService) {
                    selectionService._evaluate(layer);
                }
            });
        }
        const scaleX = instrument.getSharedVar("scaleX");
        const scaleY = instrument.getSharedVar("scaleY");
        const attrNameX = instrument.getSharedVar("attrNameX");
        const extentX = instrument.getSharedVar("extentX") ?? [0, 0];
        const extentXData = extentX.map(scaleX);
        const attrNameY = instrument.getSharedVar("attrNameY");
        const extentY = instrument.getSharedVar("extentY") ?? [0, 0];
        const extentYData = extentX.map(scaleY).reverse();
        const services = instrument.services.add("Quantitative2DSelectionService", {
            layer,
        });
        services.setSharedVar("attrNameX", attrNameX);
        services.setSharedVar("extentX", extentX);
        services.setSharedVar("attrNameY", attrNameY);
        services.setSharedVar("extentY", extentY);
        instrument.transformers
            .add("TransientRectangleTransformer", {
            transient: true,
            layer: layer.getLayerFromQueue("transientLayer"),
            sharedVar: {
                x: extentXData[0],
                y: extentYData[0],
                width: extentXData[1] - extentXData[0],
                height: extentYData[1] - extentYData[0],
                fill: "#000",
                opacity: 0.3,
            },
        })
            .add("HighlightSelection", {
            transient: true,
            layer: layer.getLayerFromQueue("selectionLayer"),
            sharedVar: {
                highlightAttrValues: instrument.getSharedVar("highlightAttrValues") || {},
            },
        });
        await Promise.all(instrument.services.results);
    },
});
Instrument.register("DataBrushXInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            async ({ event, layer, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const scaleX = instrument.getSharedVar("scaleX");
                const services = instrument.services.find("QuantitativeSelectionService");
                // services.setSharedVar("x", event.clientX, { layer });
                // services.setSharedVar("width", 1, { layer });
                // const
                // services.setSharedVar("startx", event.clientX, { layer });
                // services.setSharedVar("currentx", event.clientX, { layer });
                const layerPosX = d3.pointer(event, layer.getGraphic())[0];
                instrument.setSharedVar("layerOffsetX", event.clientX - layerPosX);
                instrument.setSharedVar("startx", event.clientX);
                instrument.setSharedVar("startLayerPosX", layerPosX);
                const newExtent = [layerPosX, layerPosX + 1].map(scaleX.invert);
                services.setSharedVar("extent", newExtent);
                instrument.transformers
                    .find("TransientRectangleTransformer")
                    .setSharedVars({
                    x: layerPosX,
                    width: 1,
                });
            },
        ],
        drag: [
            Command.initialize("drawBrushAndSelect", {
                continuous: true,
                execute: async ({ event, layer, instrument }) => {
                    if (event.changedTouches)
                        event = event.changedTouches[0];
                    const startx = instrument.getSharedVar("startx");
                    const layerOffsetX = instrument.getSharedVar("layerOffsetX");
                    const scaleX = instrument.getSharedVar("scaleX");
                    const x = Math.min(startx, event.clientX);
                    const width = Math.abs(event.clientX - startx);
                    const newExtent = [x - layerOffsetX, x - layerOffsetX + width].map(scaleX.invert);
                    // selection, currently service use client coordinates, but coordinates relative to the layer maybe more appropriate.
                    const services = instrument.services.find("QuantitativeSelectionService");
                    instrument.setSharedVar("extent", newExtent);
                    services.setSharedVar("extent", newExtent);
                    // services.setSharedVar("x", x, { layer });
                    // services.setSharedVar("width", width, {
                    //   layer,
                    // });
                    // services.setSharedVar("currentx", event.clientX, { layer });
                    await Promise.all(instrument.services.results);
                },
                feedback: [
                    async ({ event, layer, instrument }) => {
                        const startLayerPosX = instrument.getSharedVar("startLayerPosX");
                        const layerPosX = d3.pointer(event, layer.getGraphic())[0];
                        console.log(startLayerPosX, layerPosX);
                        const x = Math.min(startLayerPosX, layerPosX);
                        const width = Math.abs(layerPosX - startLayerPosX);
                        // // draw brush
                        // const baseBBox = (
                        //   layer.getGraphic().querySelector(".ig-layer-background") ||
                        //   layer.getGraphic()
                        // ).getBoundingClientRect();
                        instrument.transformers
                            .find("TransientRectangleTransformer")
                            .setSharedVars({
                            x: x,
                            width: width,
                        });
                    },
                    async ({ instrument }) => {
                        instrument.transformers.find("HighlightSelection").setSharedVars({
                            highlightAttrValues: instrument.getSharedVar("highlightAttrValues") || {},
                        });
                    },
                ],
            }),
        ],
        dragabort: [
            async ({ event, layer, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const services = instrument.services.find("SelectionService");
                services.setSharedVar("x", 0, { layer });
                services.setSharedVar("width", 0, { layer });
                services.setSharedVar("currentx", event.clientX, { layer });
                services.setSharedVar("endx", event.clientX, { layer });
                instrument.transformers
                    .find("TransientRectangleTransformer")
                    .setSharedVars({
                    x: 0,
                    width: 0,
                });
            },
        ],
    },
    preAttach: (instrument, layer) => {
        // create selectionLayer first
        const selectionLayer = layer.getLayerFromQueue("selectionLayer");
        // Sync selection layer with parent layer updates
        if (layer.onUpdate) {
            layer.onUpdate(() => {
                // console.log(
                //   "[DataBrushXInstrument] Parent layer updated. Re-evaluating selection..."
                // );
                const graphic = selectionLayer.getGraphic();
                if (graphic)
                    graphic.innerHTML = "";
                const selectionService = instrument.services.find("QuantitativeSelectionService");
                if (selectionService) {
                    selectionService._evaluate(layer);
                }
            });
        }
        //const y = instrument.getSharedVar("y") ?? 0;
        const scaleX = instrument.getSharedVar("scaleX");
        const height = instrument.getSharedVar("height") ?? layer._height;
        const y = instrument.getSharedVar("y") ?? 0;
        const attrName = instrument.getSharedVar("attrNameX");
        const extent = instrument.getSharedVar("extentX") ?? [0, 0];
        const extentData = extent.map(scaleX);
        // const attrNameY = instrument.getSharedVar("attrNameY");
        // const extentY = instrument.getSharedVar("extentY");
        const services = instrument.services.add("QuantitativeSelectionService", {
            layer,
        });
        // const bbox = layer.getGraphic().getBoundingClientRect();
        services.setSharedVar("attrName", attrName);
        services.setSharedVar("extent", extent);
        instrument.transformers
            .add("TransientRectangleTransformer", {
            transient: true,
            layer: layer.getLayerFromQueue("transientLayer"),
            sharedVar: {
                x: extentData[0],
                y: y,
                width: extentData[1] - extentData[0],
                height: height,
                fill: "#000",
                opacity: 0.3,
            },
        })
            .add("HighlightSelection", {
            transient: true,
            layer: layer.getLayerFromQueue("selectionLayer"),
            sharedVar: {
                highlightAttrValues: instrument.getSharedVar("highlightAttrValues") || {},
            },
        });
    },
});
Instrument.register("DragInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            ({ layer, event, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.services.setSharedVars({
                    x: event.clientX,
                    y: event.clientY,
                    startx: event.clientX,
                    starty: event.clientY,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    offsetx: event.offsetX,
                    offsety: event.offsetY,
                    offset: { x: 0, y: 0 },
                    skipPicking: false,
                }, { layer });
            },
        ],
        drag: [
            ({ layer, event, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const offsetX = event.clientX - instrument.services.getSharedVar("x", { layer })[0];
                const offsetY = event.clientY - instrument.services.getSharedVar("y", { layer })[0];
                instrument.setSharedVar("offsetx", offsetX, { layer });
                instrument.setSharedVar("offsety", offsetY, { layer });
                instrument.services.setSharedVars({
                    x: event.clientX,
                    y: event.clientY,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    offsetx: event.offsetX,
                    offsety: event.offsetY,
                    offset: { x: offsetX, y: offsetY },
                    skipPicking: true,
                }, { layer });
            },
        ],
        dragend: [
            ({ layer, event, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const offsetX = event.clientX - instrument.services.getSharedVar("x", { layer })[0];
                const offsetY = event.clientY - instrument.services.getSharedVar("y", { layer })[0];
                instrument.services.setSharedVars({
                    x: 0,
                    y: 0,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    endx: event.clientX,
                    endy: event.clientY,
                    offsetx: 0,
                    offsety: 0,
                    offset: { x: 0, y: 0 },
                    skipPicking: false,
                }, { layer });
                instrument.setSharedVar("offsetx", offsetX, { layer });
                instrument.setSharedVar("offsety", offsetY, { layer });
            },
            Command.initialize("Log", { execute() { } }),
        ],
        dragabort: [
            (options) => {
                let { layer, event, instrument } = options;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.services.setSharedVars({
                    x: 0,
                    y: 0,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    endx: 0,
                    endy: 0,
                    offsetx: 0,
                    offsety: 0,
                    skipPicking: false,
                }, { layer });
                instrument.emit("dragconfirm", {
                    ...options,
                    self: options.instrument,
                });
            },
        ],
    },
    preAttach: (instrument, layer) => {
        // Create default SM on layer
        instrument.services.add("SurfacePointSelectionService", {
            layer,
            sharedVar: { deepClone: instrument.getSharedVar("deepClone") },
        });
    },
});
Instrument.register("SpeechInstrument", {
    constructor: Instrument,
    interactors: ["SpeechControlInteractor"],
});
Instrument.register("KeyboardHelperBarInstrument", {
    constructor: Instrument,
    interactors: ["KeyboardPositionInteractor"],
    on: {
        begin: [() => console.log("begin")],
        left: [
            ({ event, layer, instrument }) => {
                const speed = instrument.getSharedVar("speed") || 1;
                const transientLayer = layer.getLayerFromQueue("transientLayer");
                const helperBar = transientLayer
                    .getGraphic()
                    .querySelector("line");
                const transform = getTransform(helperBar);
                const newX = transform[0] - speed;
                helperBar.setAttribute("transform", `translate(${newX}, 0)`);
                instrument.setSharedVar("barX", newX, {});
            },
        ],
        right: [
            ({ event, layer, instrument }) => {
                const speed = instrument.getSharedVar("speed") || 1;
                const transientLayer = layer.getLayerFromQueue("transientLayer");
                const helperBar = transientLayer
                    .getGraphic()
                    .querySelector("line");
                const transform = getTransform(helperBar);
                const newX = transform[0] + speed;
                helperBar.setAttribute("transform", `translate(${newX}, 0)`);
                instrument.setSharedVar("barX", newX, {});
            },
        ],
    },
    preAttach: function (instrument, layer) {
        layer.getGraphic().setAttribute("tabindex", 0);
        layer.getGraphic().focus();
        // const startX = layer.getSharedVar("startX", 0);
        const height = layer._height;
        const startPos = instrument.getSharedVar("startPos");
        const transientLayer = layer.getLayerFromQueue("transientLayer");
        const helperBar = document.createElementNS("http://www.w3.org/2000/svg", "line");
        helperBar.setAttribute("x1", startPos);
        helperBar.setAttribute("y1", "0");
        helperBar.setAttribute("x2", startPos);
        helperBar.setAttribute("y2", height);
        helperBar.setAttribute("stroke", `black`);
        helperBar.setAttribute("stroke-width", `1px`);
        transientLayer.getGraphic().append(helperBar);
    },
});
/** only apply to linear scale. should record currentX as x in domain if fixRange is true */
Instrument.register("PanInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            ({ layer, event, instrument }) => {
                const modifierKey = instrument.getSharedVar("modifierKey");
                if (!checkModifier(event, modifierKey)) {
                    instrument.setSharedVar("interactionValid", false);
                    return;
                }
                instrument.setSharedVar("interactionValid", true);
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.setSharedVar("startx", event.clientX);
                instrument.setSharedVar("starty", event.clientY);
                let transformers = instrument.transformers;
                if (!transformers.length) {
                    transformers = GraphicalTransformer.findTransformerByLayer(layer);
                }
                transformers.forEach((transformer) => {
                    const sx = transformer.getSharedVar("scaleX");
                    const sy = transformer.getSharedVar("scaleY");
                    if (sx) {
                        transformer.setSharedVar("$$scaleX", sx.copy());
                        // transformer.setSharedVar("startDomainX", sx.domain());
                        // transformer.setSharedVar("startRangeX", sx.range());
                    }
                    if (sy) {
                        transformer.setSharedVar("$$scaleY", sy.copy());
                        // transformer.setSharedVar("startDomainY", sy.domain());
                        // transformer.setSharedVar("startRangeY", sy.range());
                    }
                });
            },
        ],
        drag: [
            async ({ layer, event, instrument, transformer }) => {
                console.log("drag");
                if (!instrument.getSharedVar("interactionValid"))
                    return;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                let transformers = instrument.transformers;
                if (!transformers.length) {
                    transformers = GraphicalTransformer.findTransformerByLayer(layer);
                }
                const startx = instrument.getSharedVar("startx");
                const starty = instrument.getSharedVar("starty");
                const fixRange = instrument.getSharedVar("fixRange") ?? false;
                transformers.forEach((transformer) => {
                    const sx = transformer.getSharedVar("scaleX");
                    const sy = transformer.getSharedVar("scaleY");
                    if (fixRange) {
                        if (sx) {
                            const scaleXOrigin = transformer.getSharedVar("$$scaleX");
                            const startRangeX = scaleXOrigin.range();
                            const newRangeX = startRangeX.map((x, i) => x - event.clientX + startx);
                            const newDomain = newRangeX.map((x) => scaleXOrigin.invert(x));
                            sx.domain(newDomain);
                            transformer.setSharedVar("scaleX", sx);
                        }
                        if (sy) {
                            const scaleYOrigin = transformer.getSharedVar("$$scaleY");
                            const startRangeY = scaleYOrigin.range();
                            const newRangeY = startRangeY.map((y, i) => y - event.clientY + starty);
                            const newDomain = newRangeY.map((y) => scaleYOrigin.invert(y));
                            sy.domain(newDomain);
                            transformer.setSharedVar("scaleY", sy);
                        }
                    }
                    else {
                        if (sx) {
                            const startRangeX = transformer.getSharedVar("$$scaleX").range();
                            const newRangeX = startRangeX.map((x, i) => x + event.clientX - startx);
                            sx.range(newRangeX);
                            transformer.setSharedVar("scaleX", sx);
                        }
                        if (sy) {
                            // const newRangeY = sy.range().map((y) => y + offsetY);
                            const startRangeY = transformer.getSharedVar("$$scaleY").range();
                            const newRangeY = startRangeY.map((y, i) => y + event.clientY - starty);
                            sy.range(newRangeY);
                            transformer.setSharedVar("scaleY", sy);
                        }
                    }
                });
            },
        ],
        dragend: [Command.initialize("Log", { execute() { } })],
        dragabort: [
            ({ layer, event, instrument, transformer }) => {
                // if (event.changedTouches) event = event.changedTouches[0];
                // const sx = transformer.getTransformation("$$scaleX");
                // const sy = transformer.getTransformation("$$scaleY");
                // instrument.setSharedVar("startx", event.clientX);
                // instrument.setSharedVar("starty", event.clientY);
                // instrument.setSharedVar("currentx", event.clientX);
                // instrument.setSharedVar("currenty", event.clientY);
                // if (sx) {
                //   transformer.setTransformation("scaleX", sx);
                //   transformer.setTransformation("$scaleX", sx);
                // }
                // if (sy) {
                //   transformer.setTransformation("scaleY", sy);
                //   transformer.setTransformation("$scaleY", sy);
                // }
                // layer.getLayerFromQueue("selectionLayer").getGraphic().innerHTML = "";
                // layer.getLayerFromQueue("transientLayer").getGraphic().innerHTML = "";
            },
        ],
    },
});
Instrument.register("PanXInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            ({ layer, event, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.setSharedVar("startx", event.clientX);
                // instrument.setSharedVar("starty", event.clientY);
                const transformers = instrument.transformers;
                transformers.forEach((transformer) => {
                    const sx = transformer.getSharedVar("scaleX");
                    // const sy = transformer.getSharedVar("scaleY");
                    if (sx) {
                        transformer.setSharedVar("$$scaleX", sx.copy());
                        // transformer.setSharedVar("startDomainX", sx.domain());
                        // transformer.setSharedVar("startRangeX", sx.range());
                    }
                    // if (sy) {
                    // transformer.setSharedVar("$$scaleY", sy.copy());
                    // transformer.setSharedVar("startDomainY", sy.domain());
                    // transformer.setSharedVar("startRangeY", sy.range());
                    // }
                });
            },
        ],
        drag: [
            async ({ layer, event, instrument, transformer }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const transformers = instrument.transformers;
                const startx = instrument.getSharedVar("startx");
                // const starty = instrument.getSharedVar("starty");
                const fixRange = instrument.getSharedVar("fixRange") ?? false;
                transformers.forEach((transformer) => {
                    const sx = transformer.getSharedVar("scaleX");
                    // const sy = transformer.getSharedVar("scaleY");
                    if (fixRange) {
                        if (sx) {
                            const scaleXOrigin = transformer.getSharedVar("$$scaleX");
                            const startRangeX = scaleXOrigin.range();
                            const newRangeX = startRangeX.map((x, i) => x - event.clientX + startx);
                            const newDomain = newRangeX.map((x) => scaleXOrigin.invert(x));
                            sx.domain(newDomain);
                            transformer.setSharedVar("scaleX", sx);
                        }
                        // if (sy) {
                        //   const scaleYOrigin = transformer.getSharedVar("$$scaleY");
                        //   const startRangeY = scaleYOrigin.range();
                        //   const newRangeY = startRangeY.map((y, i) => y - event.clientY + starty);
                        //   const newDomain = newRangeY.map(y => scaleYOrigin.invert(y));
                        //   sy.domain(newDomain);
                        //   transformer.setSharedVar("scaleY", sy);
                        // }
                    }
                    else {
                        if (sx) {
                            const startRangeX = transformer.getSharedVar("$$scaleX").range();
                            const newRangeX = startRangeX.map((x, i) => x + event.clientX - startx);
                            sx.range(newRangeX);
                            transformer.setSharedVar("scaleX", sx);
                        }
                        // if (sy) {
                        //   // const newRangeY = sy.range().map((y) => y + offsetY);
                        //   const startRangeY = transformer.getSharedVar("$$scaleY").range();
                        //   const newRangeY = startRangeY.map((y, i) => y + event.clientY - starty);
                        //   sy.range(newRangeY);
                        //   transformer.setSharedVar("scaleY", sy);
                        // }
                    }
                });
            },
        ],
        dragabort: [
            ({ layer, event, instrument, transformer }) => {
                // if (event.changedTouches) event = event.changedTouches[0];
                // const sx = transformer.getTransformation("$$scaleX");
                // const sy = transformer.getTransformation("$$scaleY");
                // instrument.setSharedVar("startx", event.clientX);
                // instrument.setSharedVar("starty", event.clientY);
                // instrument.setSharedVar("currentx", event.clientX);
                // instrument.setSharedVar("currenty", event.clientY);
                // if (sx) {
                //   transformer.setTransformation("scaleX", sx);
                //   transformer.setTransformation("$scaleX", sx);
                // }
                // if (sy) {
                //   transformer.setTransformation("scaleY", sy);
                //   transformer.setTransformation("$scaleY", sy);
                // }
                // layer.getLayerFromQueue("selectionLayer").getGraphic().innerHTML = "";
                // layer.getLayerFromQueue("transientLayer").getGraphic().innerHTML = "";
            },
        ],
    },
});
Instrument.register("GeometricZoomInstrument", {
    constructor: Instrument,
    interactors: ["MouseWheelInteractor"],
    on: {
        wheel: [
            ({ layer, instrument, event }) => {
                // const modifierKey = instrument.getSharedVar("modifierKey");
                // if (!checkModifier(event, modifierKey)) return;
                console.log("weee`2`ee");
                const layerGraphic = layer.getGraphic();
                const layerRoot = d3.select(layerGraphic);
                let transformers = instrument.transformers;
                if (!transformers.length) {
                    transformers = GraphicalTransformer.findTransformerByLayer(layer);
                }
                instrument.setSharedVar("currentx", event.offsetX);
                instrument.setSharedVar("currenty", event.offsetY);
                let delta = event.deltaY;
                instrument.setSharedVar("delta", delta);
                let cumulativeDelta = instrument.getSharedVar("cumulativeDelta", {
                    defaultValue: 0,
                });
                cumulativeDelta += delta;
                instrument.setSharedVar("cumulativeDelta", cumulativeDelta);
                delta /= 1000;
                const [x, y] = d3.pointer(event, layerGraphic);
                const offsetX = instrument.getSharedVar("centroidX") || x;
                const offsetY = instrument.getSharedVar("centroidY") || y;
                const fixRange = instrument.getSharedVar("fixRange") ?? false;
                transformers.forEach((transformer) => {
                    const sx = transformer.getSharedVar("scaleX");
                    const sy = transformer.getSharedVar("scaleY");
                    if (fixRange) {
                        if (sx) {
                            if (sx.type === "time") {
                                const offsetXDomain = sx.invert(offsetX);
                                sx.domain(sx
                                    .domain()
                                    .map((d) => new Date(d.getTime() - offsetXDomain.getTime()))
                                    .map((d) => new Date(d.getTime() * Math.exp(-delta)))
                                    .map((d) => new Date(d.getTime() + offsetXDomain.getTime())));
                            }
                            else {
                                const offsetXDomain = sx.invert(offsetX);
                                sx.domain(sx
                                    .domain()
                                    .map((d) => d - offsetXDomain)
                                    .map((d) => d * Math.exp(-delta))
                                    .map((d) => d + offsetXDomain));
                            }
                            transformers.forEach((transformer) => transformer.setSharedVar("scaleX", sx));
                        }
                        if (sy) {
                            if (sy.type === "time") {
                                const offsetYDomain = sy.invert(offsetY);
                                sy.domain(sy
                                    .domain()
                                    .map((d) => new Date(d.getTime() - offsetYDomain.getTime()))
                                    .map((d) => new Date(d.getTime() * Math.exp(-delta)))
                                    .map((d) => new Date(d.getTime() + offsetYDomain.getTime())));
                            }
                            else {
                                const offsetYDomain = sy.invert(offsetY);
                                sy.domain(sy
                                    .domain()
                                    .map((d) => d - offsetYDomain)
                                    .map((d) => d * Math.exp(-delta))
                                    .map((d) => d + offsetYDomain));
                            }
                            transformers.forEach((transformer) => transformer.setSharedVar("scaleY", sy));
                        }
                    }
                    else {
                        if (sx) {
                            const newRangeX = sx
                                .range()
                                .map((x) => (x - offsetX) * Math.exp(delta) + offsetX);
                            sx.range(newRangeX);
                            transformer.setSharedVar("scaleX", sx);
                        }
                        if (sy) {
                            const newRangeY = sy
                                .range()
                                .map((y) => (y - offsetY) * Math.exp(delta) + offsetY);
                            sy.range(newRangeY);
                            transformer.setSharedVar("scaleY", sy);
                        }
                    }
                });
                // if (fixRange) {
                //   if (sx) {
                //     const scaleX = sx
                //       .copy()
                //       .domain(
                //         sx.range().map((x) => (x - offsetX) * Math.exp(delta) + offsetX)
                //       )
                //       .range(sx.domain());
                //     if (scaleX.clamp) scaleX.clamp(false);
                //     scaleX.domain(sx.range().map((x) => scaleX(x))).range(sx.range());
                //     transformers.forEach((transformer) => transformer.setSharedVar("scaleX", scaleX));
                //   }
                //   if (sy) {
                //     const scaleY = sy
                //       .copy()
                //       .domain(
                //         sy.range().map((y) => (y - offsetY) * Math.exp(delta) + offsetY)
                //       )
                //       .range(sy.domain());
                //     if (scaleY.clamp) scaleY.clamp(false);
                //     scaleY.domain(sy.range().map((y) => scaleY(y))).range(sy.range());
                //     transformers.forEach((transformer) => transformer.setSharedVar("scaleY", scaleY));
                //   }
                // }
                // else {
                //   if (sx) {
                //     const proxyRaw = (
                //       raw: Transformation & { $origin: Transformation }
                //     ) =>
                //       new Proxy(raw, {
                //         get(target, path) {
                //           if (path in target) return target[path];
                //           if (path === "range")
                //             return (...args) =>
                //               (target.$origin as any)
                //                 .range(
                //                   ...args.map(
                //                     (x) => (x - offsetX) / Math.exp(delta) + offsetX
                //                   )
                //                 )
                //                 .map((x) => (x - offsetX) * Math.exp(delta) + offsetX);
                //           if (path === "bandwidth" && "bandwidth" in target.$origin) {
                //             return () =>
                //               (target.$origin as any).bandwidth() * Math.exp(delta);
                //           }
                //           return target.$origin[path];
                //         },
                //         apply(target, thisArg, argArray) {
                //           return target.apply(thisArg, argArray);
                //         },
                //         has(target, path) {
                //           return path in target || path in target.$origin;
                //         },
                //       });
                //     const scaleXRaw = (domain) =>
                //       (scaleXRaw.$origin(domain) - offsetX) * Math.exp(delta) + offsetX;
                //     scaleXRaw.invert = (range) =>
                //       scaleXRaw.$origin.invert(
                //         (range - offsetX) / Math.exp(delta) + offsetX
                //       );
                //     scaleXRaw.$origin = sx;
                //     scaleXRaw.copy = () => {
                //       const anotherScaleXRaw = (domain) =>
                //         (anotherScaleXRaw.$origin(domain) - offsetX) * Math.exp(delta) +
                //         offsetX;
                //       Object.assign(anotherScaleXRaw, scaleXRaw);
                //       anotherScaleXRaw.$origin = sx.copy();
                //       anotherScaleXRaw.invert = (range) =>
                //         anotherScaleXRaw.$origin.invert(
                //           (range - offsetX) / Math.exp(delta) + offsetX
                //         );
                //       return proxyRaw(anotherScaleXRaw as any);
                //     };
                //     const scaleX = proxyRaw(scaleXRaw);
                //     transformer.setTransformation("scaleX", scaleX);
                //   }
                //   if (sy) {
                //     const proxyRaw = (
                //       raw: Transformation & { $origin: Transformation }
                //     ) =>
                //       new Proxy(raw, {
                //         get(target, path) {
                //           if (path in target) return target[path];
                //           if (path === "range")
                //             return (...args) =>
                //               (target.$origin as any)
                //                 .range(...args)
                //                 .map((y) => (y - offsetY) * Math.exp(delta) + offsetY);
                //           if (path === "bandwidth" && "bandwidth" in target.$origin) {
                //             return () =>
                //               (target.$origin as any).bandwidth() * Math.exp(delta);
                //           }
                //           return target.$origin[path];
                //         },
                //         apply(target, thisArg, argArray) {
                //           return target.apply(thisArg, argArray);
                //         },
                //         has(target, path) {
                //           return path in target || path in target.$origin;
                //         },
                //       });
                //     const scaleYRaw = (domain) =>
                //       (scaleYRaw.$origin(domain) - offsetY) * Math.exp(delta) + offsetY;
                //     scaleYRaw.invert = (range) =>
                //       scaleYRaw.$origin.invert(
                //         (range - offsetY) / Math.exp(delta) + offsetY
                //       );
                //     scaleYRaw.$origin = sy;
                //     scaleYRaw.copy = () => {
                //       const anotherScaleYRaw = (domain) =>
                //         (anotherScaleYRaw.$origin(domain) - offsetY) * Math.exp(delta) +
                //         offsetY;
                //       Object.assign(anotherScaleYRaw, scaleYRaw);
                //       anotherScaleYRaw.invert = (range) =>
                //         anotherScaleYRaw.$origin.invert(
                //           (range - offsetY) / Math.exp(delta) + offsetY
                //         );
                //       anotherScaleYRaw.$origin = sy.copy();
                //       return proxyRaw(anotherScaleYRaw as any);
                //     };
                //     const scaleY = proxyRaw(scaleYRaw);
                //     transformer.setTransformation("scaleY", scaleY);
                //   }
                // }
            },
        ],
        abort: [
            ({ layer, event, instrument, transformer }) => {
                // const sx = transformer.getTransformation("$$scaleX");
                // const sy = transformer.getTransformation("$$scaleY");
                // instrument.setSharedVar("delta", 0);
                // instrument.setSharedVar("currentx", event.offsetX);
                // instrument.setSharedVar("currenty", event.offsetY);
                // if (sx) {
                //   transformer.setTransformation("scaleX", sx);
                // }
                // if (sy) {
                //   transformer.setTransformation("scaleY", sy);
                // }
                // layer.getLayerFromQueue("selectionLayer").getGraphic().innerHTML = "";
                // layer.getLayerFromQueue("transientLayer").getGraphic().innerHTML = "";
            },
        ],
    },
});
Instrument.register("SemanticZoomInstrument", {
    constructor: Instrument,
    interactors: ["MouseWheelInteractor"],
    sharedVar: {
        currentLevel: 0,
    },
    on: {
        wheel: [
            ({ layer, instrument, event }) => {
                const layerGraphic = layer.getGraphic();
                const layerRoot = d3.select(layerGraphic);
                let transformers = instrument.transformers;
                if (!transformers.length) {
                    transformers = GraphicalTransformer.findTransformerByLayer(layer);
                }
                const scaleLevels = instrument.getSharedVar("scaleLevels");
                let currentLevel = instrument.getSharedVar("currentLevel");
                currentLevel += Math.sign(event.deltaY);
                instrument.setSharedVar("currentLevel", currentLevel);
                if (typeof scaleLevels === "object") {
                    const closestLevel = Object.keys(scaleLevels).reduce(function (prev, curr) {
                        return Math.abs(parseInt(curr) - currentLevel) <
                            Math.abs(parseInt(prev) - currentLevel)
                            ? curr
                            : prev;
                    });
                    transformers.forEach((t) => t.setSharedVars(scaleLevels[closestLevel]));
                }
                instrument.setSharedVar("currentx", event.offsetX);
                instrument.setSharedVar("currenty", event.offsetY);
                let delta = event.deltaY;
                instrument.setSharedVar("delta", delta);
                let cumulativeDelta = instrument.getSharedVar("cumulativeDelta", {
                    defaultValue: 0,
                });
                cumulativeDelta += delta;
                instrument.setSharedVar("cumulativeDelta", cumulativeDelta);
                delta /= 1000;
                const [x, y] = d3.pointer(event, layerGraphic);
                const offsetX = instrument.getSharedVar("centroidX") || x;
                const offsetY = instrument.getSharedVar("centroidY") || y;
                const fixRange = instrument.getSharedVar("fixRange") ?? false;
                transformers.forEach((transformer) => {
                    const sx = transformer.getSharedVar("scaleX");
                    const sy = transformer.getSharedVar("scaleY");
                    if (fixRange) {
                        if (sx) {
                            if (sx.type === "time") {
                                const offsetXDomain = sx.invert(offsetX);
                                sx.domain(sx
                                    .domain()
                                    .map((d) => new Date(d.getTime() - offsetXDomain.getTime()))
                                    .map((d) => new Date(d.getTime() * Math.exp(-delta)))
                                    .map((d) => new Date(d.getTime() + offsetXDomain.getTime())));
                            }
                            else {
                                const offsetXDomain = sx.invert(offsetX);
                                sx.domain(sx
                                    .domain()
                                    .map((d) => d - offsetXDomain)
                                    .map((d) => d * Math.exp(-delta))
                                    .map((d) => d + offsetXDomain));
                            }
                            transformers.forEach((transformer) => transformer.setSharedVar("scaleX", sx));
                        }
                        if (sy) {
                            if (sy.type === "time") {
                                const offsetYDomain = sy.invert(offsetY);
                                sy.domain(sy
                                    .domain()
                                    .map((d) => new Date(d.getTime() - offsetYDomain.getTime()))
                                    .map((d) => new Date(d.getTime() * Math.exp(-delta)))
                                    .map((d) => new Date(d.getTime() + offsetYDomain.getTime())));
                            }
                            else {
                                const offsetYDomain = sy.invert(offsetY);
                                sy.domain(sy
                                    .domain()
                                    .map((d) => d - offsetYDomain)
                                    .map((d) => d * Math.exp(-delta))
                                    .map((d) => d + offsetYDomain));
                            }
                            transformers.forEach((transformer) => transformer.setSharedVar("scaleY", sy));
                        }
                    }
                    else {
                        if (sx) {
                            const newRangeX = sx
                                .range()
                                .map((x) => (x - offsetX) * Math.exp(delta) + offsetX);
                            sx.range(newRangeX);
                            transformer.setSharedVar("scaleX", sx);
                        }
                        if (sy) {
                            const newRangeY = sy
                                .range()
                                .map((y) => (y - offsetY) * Math.exp(delta) + offsetY);
                            sy.range(newRangeY);
                            transformer.setSharedVar("scaleY", sy);
                        }
                    }
                });
                // if (fixRange) {
                //   if (sx) {
                //     const scaleX = sx
                //       .copy()
                //       .domain(
                //         sx.range().map((x) => (x - offsetX) * Math.exp(delta) + offsetX)
                //       )
                //       .range(sx.domain());
                //     if (scaleX.clamp) scaleX.clamp(false);
                //     scaleX.domain(sx.range().map((x) => scaleX(x))).range(sx.range());
                //     transformers.forEach((transformer) => transformer.setSharedVar("scaleX", scaleX));
                //   }
                //   if (sy) {
                //     const scaleY = sy
                //       .copy()
                //       .domain(
                //         sy.range().map((y) => (y - offsetY) * Math.exp(delta) + offsetY)
                //       )
                //       .range(sy.domain());
                //     if (scaleY.clamp) scaleY.clamp(false);
                //     scaleY.domain(sy.range().map((y) => scaleY(y))).range(sy.range());
                //     transformers.forEach((transformer) => transformer.setSharedVar("scaleY", scaleY));
                //   }
                // }
                // else {
                //   if (sx) {
                //     const proxyRaw = (
                //       raw: Transformation & { $origin: Transformation }
                //     ) =>
                //       new Proxy(raw, {
                //         get(target, path) {
                //           if (path in target) return target[path];
                //           if (path === "range")
                //             return (...args) =>
                //               (target.$origin as any)
                //                 .range(
                //                   ...args.map(
                //                     (x) => (x - offsetX) / Math.exp(delta) + offsetX
                //                   )
                //                 )
                //                 .map((x) => (x - offsetX) * Math.exp(delta) + offsetX);
                //           if (path === "bandwidth" && "bandwidth" in target.$origin) {
                //             return () =>
                //               (target.$origin as any).bandwidth() * Math.exp(delta);
                //           }
                //           return target.$origin[path];
                //         },
                //         apply(target, thisArg, argArray) {
                //           return target.apply(thisArg, argArray);
                //         },
                //         has(target, path) {
                //           return path in target || path in target.$origin;
                //         },
                //       });
                //     const scaleXRaw = (domain) =>
                //       (scaleXRaw.$origin(domain) - offsetX) * Math.exp(delta) + offsetX;
                //     scaleXRaw.invert = (range) =>
                //       scaleXRaw.$origin.invert(
                //         (range - offsetX) / Math.exp(delta) + offsetX
                //       );
                //     scaleXRaw.$origin = sx;
                //     scaleXRaw.copy = () => {
                //       const anotherScaleXRaw = (domain) =>
                //         (anotherScaleXRaw.$origin(domain) - offsetX) * Math.exp(delta) +
                //         offsetX;
                //       Object.assign(anotherScaleXRaw, scaleXRaw);
                //       anotherScaleXRaw.$origin = sx.copy();
                //       anotherScaleXRaw.invert = (range) =>
                //         anotherScaleXRaw.$origin.invert(
                //           (range - offsetX) / Math.exp(delta) + offsetX
                //         );
                //       return proxyRaw(anotherScaleXRaw as any);
                //     };
                //     const scaleX = proxyRaw(scaleXRaw);
                //     transformer.setTransformation("scaleX", scaleX);
                //   }
                //   if (sy) {
                //     const proxyRaw = (
                //       raw: Transformation & { $origin: Transformation }
                //     ) =>
                //       new Proxy(raw, {
                //         get(target, path) {
                //           if (path in target) return target[path];
                //           if (path === "range")
                //             return (...args) =>
                //               (target.$origin as any)
                //                 .range(...args)
                //                 .map((y) => (y - offsetY) * Math.exp(delta) + offsetY);
                //           if (path === "bandwidth" && "bandwidth" in target.$origin) {
                //             return () =>
                //               (target.$origin as any).bandwidth() * Math.exp(delta);
                //           }
                //           return target.$origin[path];
                //         },
                //         apply(target, thisArg, argArray) {
                //           return target.apply(thisArg, argArray);
                //         },
                //         has(target, path) {
                //           return path in target || path in target.$origin;
                //         },
                //       });
                //     const scaleYRaw = (domain) =>
                //       (scaleYRaw.$origin(domain) - offsetY) * Math.exp(delta) + offsetY;
                //     scaleYRaw.invert = (range) =>
                //       scaleYRaw.$origin.invert(
                //         (range - offsetY) / Math.exp(delta) + offsetY
                //       );
                //     scaleYRaw.$origin = sy;
                //     scaleYRaw.copy = () => {
                //       const anotherScaleYRaw = (domain) =>
                //         (anotherScaleYRaw.$origin(domain) - offsetY) * Math.exp(delta) +
                //         offsetY;
                //       Object.assign(anotherScaleYRaw, scaleYRaw);
                //       anotherScaleYRaw.invert = (range) =>
                //         anotherScaleYRaw.$origin.invert(
                //           (range - offsetY) / Math.exp(delta) + offsetY
                //         );
                //       anotherScaleYRaw.$origin = sy.copy();
                //       return proxyRaw(anotherScaleYRaw as any);
                //     };
                //     const scaleY = proxyRaw(scaleYRaw);
                //     transformer.setTransformation("scaleY", scaleY);
                //   }
                // }
            },
        ],
        abort: [
            ({ layer, event, instrument, transformer }) => {
                // const sx = transformer.getTransformation("$$scaleX");
                // const sy = transformer.getTransformation("$$scaleY");
                // instrument.setSharedVar("delta", 0);
                // instrument.setSharedVar("currentx", event.offsetX);
                // instrument.setSharedVar("currenty", event.offsetY);
                // if (sx) {
                //   transformer.setTransformation("scaleX", sx);
                // }
                // if (sy) {
                //   transformer.setTransformation("scaleY", sy);
                // }
                // layer.getLayerFromQueue("selectionLayer").getGraphic().innerHTML = "";
                // layer.getLayerFromQueue("transientLayer").getGraphic().innerHTML = "";
            },
        ],
    },
    postUse(instrument, layer) {
        const scaleLevels = instrument.getSharedVar("scaleLevels");
        const transformers = instrument.transformers;
        const currentLevel = instrument.getSharedVar("currentLevel");
        if (typeof scaleLevels === "object") {
            const closestLevel = Object.keys(scaleLevels).reduce(function (prev, curr) {
                return Math.abs(parseInt(curr) - currentLevel) <
                    Math.abs(parseInt(prev) - currentLevel)
                    ? curr
                    : prev;
            });
            transformers.setSharedVars(scaleLevels[closestLevel]);
        }
    },
});
Instrument.register("ZoomXInstrument", {
    constructor: Instrument,
    interactors: ["MouseWheelInteractor"],
    on: {
        wheel: [
            ({ layer, instrument, event }) => {
                const layerGraphic = layer.getGraphic();
                const layerRoot = d3.select(layerGraphic);
                const transformers = instrument.transformers;
                instrument.setSharedVar("currentx", event.offsetX);
                // instrument.setSharedVar("currenty", event.offsetY);
                let delta = event.deltaY;
                instrument.setSharedVar("delta", delta);
                let cumulativeDelta = instrument.getSharedVar("cumulativeDelta", {
                    defaultValue: 0,
                });
                cumulativeDelta += delta;
                instrument.setSharedVar("cumulativeDelta", cumulativeDelta);
                delta /= 1000;
                const [x, y] = d3.pointer(event, layerGraphic);
                const offsetX = instrument.getSharedVar("centroidX") || x;
                // const offsetY = instrument.getSharedVar("centroidY") || y;
                const fixRange = instrument.getSharedVar("fixRange") ?? false;
                transformers.forEach((transformer) => {
                    const sx = transformer.getSharedVar("scaleX");
                    // const sy = transformer.getSharedVar("scaleY");
                    if (fixRange) {
                        if (sx) {
                            const offsetXDomain = sx.invert(offsetX);
                            sx.domain(sx
                                .domain()
                                .map((d) => d - offsetXDomain)
                                .map((d) => d * Math.exp(-delta))
                                .map((d) => d + offsetXDomain));
                            transformers.forEach((transformer) => transformer.setSharedVar("scaleX", sx));
                        }
                        // if (sy) {
                        //   const offsetYDomain = sy.invert(offsetY);
                        //   sy.domain(sy
                        //     .domain()
                        //     .map(d => d - offsetYDomain)
                        //     .map(d => d * Math.exp(-delta))
                        //     .map(d => d + offsetYDomain));
                        //   transformers.forEach((transformer) => transformer.setSharedVar("scaleY", sy));
                        // }
                    }
                    else {
                        if (sx) {
                            const newRangeX = sx
                                .range()
                                .map((x) => (x - offsetX) * Math.exp(delta) + offsetX);
                            sx.range(newRangeX);
                            transformer.setSharedVar("scaleX", sx);
                        }
                        // if (sy) {
                        //   const newRangeY = sy.range().map((y) => (y - offsetY) * Math.exp(delta) + offsetY);
                        //   sy.range(newRangeY);
                        //   transformer.setSharedVar("scaleY", sy);
                        // }
                    }
                });
            },
        ],
        abort: [
            ({ layer, event, instrument, transformer }) => {
                // const sx = transformer.getTransformation("$$scaleX");
                // const sy = transformer.getTransformation("$$scaleY");
                // instrument.setSharedVar("delta", 0);
                // instrument.setSharedVar("currentx", event.offsetX);
                // instrument.setSharedVar("currenty", event.offsetY);
                // if (sx) {
                //   transformer.setTransformation("scaleX", sx);
                // }
                // if (sy) {
                //   transformer.setTransformation("scaleY", sy);
                // }
                // layer.getLayerFromQueue("selectionLayer").getGraphic().innerHTML = "";
                // layer.getLayerFromQueue("transientLayer").getGraphic().innerHTML = "";
            },
        ],
    },
});
// function getTransformMatrix(transform: string){
//   const regex = /.*matrix\t*(\t*\t*).*/;
//   return tran
// }
Instrument.register("ReorderInstrument", {
    constructor: Instrument,
    interactors: ["MouseTraceInteractor", "TouchTraceInteractor"],
    on: {
        dragstart: [
            ({ layer, event, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.services.setSharedVars({
                    x: event.clientX,
                    y: event.clientY,
                    startx: event.clientX,
                    starty: event.clientY,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    offsetx: event.offsetX,
                    offsety: event.offsetY,
                    offset: { x: 0, y: 0 },
                    skipPicking: false,
                }, { layer });
            },
        ],
        drag: [
            ({ layer, event, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const offsetX = event.clientX - instrument.services.getSharedVar("x", { layer })[0];
                const offsetY = event.clientY - instrument.services.getSharedVar("y", { layer })[0];
                instrument.setSharedVar("offsetx", offsetX, { layer });
                instrument.setSharedVar("offsety", offsetY, { layer });
                instrument.services.setSharedVars({
                    x: event.clientX,
                    y: event.clientY,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    offsetx: event.offsetX,
                    offsety: event.offsetY,
                    offset: { x: offsetX, y: offsetY },
                    skipPicking: true,
                }, { layer });
            },
        ],
        dragend: [
            ({ layer, event, instrument }) => {
                if (event.changedTouches)
                    event = event.changedTouches[0];
                const offsetX = event.clientX - instrument.services.getSharedVar("x", { layer })[0];
                const offsetY = event.clientY - instrument.services.getSharedVar("y", { layer })[0];
                instrument.services.setSharedVars({
                    x: 0,
                    y: 0,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    endx: event.clientX,
                    endy: event.clientY,
                    offsetx: 0,
                    offsety: 0,
                    offset: { x: 0, y: 0 },
                    skipPicking: false,
                }, { layer });
                instrument.setSharedVar("offsetx", offsetX, { layer });
                instrument.setSharedVar("offsety", offsetY, { layer });
            },
            Command.initialize("Log", { execute() { } }),
        ],
        dragabort: [
            (options) => {
                let { layer, event, instrument } = options;
                if (event.changedTouches)
                    event = event.changedTouches[0];
                instrument.services.setSharedVars({
                    x: 0,
                    y: 0,
                    currentx: event.clientX,
                    currenty: event.clientY,
                    endx: 0,
                    endy: 0,
                    offsetx: 0,
                    offsety: 0,
                    skipPicking: false,
                }, { layer });
                instrument.emit("dragconfirm", {
                    ...options,
                    self: options.instrument,
                });
            },
        ],
    },
    preAttach: (instrument, layer) => {
        // Create default SM on layer
        instrument.services.add("SurfacePointSelectionService", {
            layer,
            sharedVar: { deepClone: instrument.getSharedVar("deepClone") },
        });
    },
});
